import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

export const FORCE_PUSH_COMPLETED_EVENT = "force_push_completed";

/**
 * One column of an item-view force push has finished: every orchestration job that click
 * created for that column is terminal. Emitted by ms-utils into the `user:<id>` room on behalf
 * of simb-orchestration's force-push notifier.
 */
export interface ForcePushCompletedEvent {
  userId: string;
  requestId: string;
  origin?: "item_view" | "resync_all";
  columnKey: string;
  columnId?: string;
  columnTitle?: string;
  itemId?: string;
  summary: { total: number; done: number; dead: number };
  reason: "no_sync_group" | null;
  message: string;
}

interface UseForcePushStreamArgs {
  userId: string | number | undefined;
  accountId?: string | number;
  onCompleted: (event: ForcePushCompletedEvent) => void;
  // Room emits are not buffered: a completion fired while the socket was down is lost, so
  // the consumer reconciles anything still pending here.
  onReconnect?: () => void;
  enabled?: boolean;
}

// Subscribes to the ms-utils notification socket and forwards force-push completions
// addressed to this user. Mirrors useDiscrepancyStream.
const useForcePushStream = ({
  userId,
  accountId,
  onCompleted,
  onReconnect,
  enabled = true,
}: UseForcePushStreamArgs) => {
  // Keep latest callbacks in refs so the socket effect does not reconnect on every render.
  const onCompletedRef = useRef(onCompleted);
  const onReconnectRef = useRef(onReconnect);
  onCompletedRef.current = onCompleted;
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!enabled || !userId) return;

    const url = `${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`;
    const socket: Socket = io(url);

    // The server only emits to this user's room; the guard is defense in depth against a
    // misrouted emit settling another user's click.
    const isForThisUser = (incomingUserId: string | number | undefined): boolean =>
      String(incomingUserId) === String(userId);

    socket.on(FORCE_PUSH_COMPLETED_EVENT, (data: ForcePushCompletedEvent) => {
      if (isForThisUser(data?.userId)) onCompletedRef.current(data);
    });

    socket.io.on("reconnect", () => {
      onReconnectRef.current?.();
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId, enabled]);
};

export default useForcePushStream;
